import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { OnboardingWizard } from './OnboardingWizard';

/**
 * Global component that checks if the logged-in user has pending 
 * speaker or volunteer onboarding and shows the wizard.
 * Mount this in your layout or App.
 */
export const SpeakerVolunteerOnboarding = () => {
  const [userId, setUserId] = useState<string | null>(null);
  const [pending, setPending] = useState<{
    type: 'speaker' | 'volunteer';
    entityId: string;
    eventName: string;
  } | null>(null);
  const [showWizard, setShowWizard] = useState(false);

  useEffect(() => {
    const init = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        setUserId(user.id);
        checkPending(user.id);
      }
    };
    init();
  }, []);

  const checkPending = async (uid: string) => {
    try {
      // Check confirmed speakers
      const { data: speakerEntries } = await supabase
        .from('event_speakers')
        .select('event_id')
        .eq('user_id', uid)
        .not('speaker_type', 'in', '("organizer","co_organizer","lead_organizer")');

      if (speakerEntries && speakerEntries.length > 0) {
        const eventIds = speakerEntries.map(s => s.event_id);
        const { data: completions } = await supabase
          .from('onboarding_completions')
          .select('entity_id')
          .eq('user_id', uid)
          .eq('onboarding_type', 'speaker');

        const completedIds = new Set((completions || []).map((c: any) => c.entity_id));
        const uncompleted = eventIds.find(id => !completedIds.has(id));

        if (uncompleted) {
          const { data: evt } = await supabase
            .from('festivals')
            .select('name')
            .eq('id', uncompleted)
            .single();

          setPending({
            type: 'speaker',
            entityId: uncompleted,
            eventName: (evt as any)?.name || 'the event',
          });
          setShowWizard(true);
          return;
        }
      }

      // Check approved volunteers
      const { data: volEntries } = await supabase
        .from('volunteer_applications')
        .select('event_id')
        .eq('user_id', uid)
        .eq('status', 'approved');

      if (volEntries && volEntries.length > 0) {
        const eventIds = volEntries.map(v => v.event_id);
        const { data: completions } = await supabase
          .from('onboarding_completions')
          .select('entity_id')
          .eq('user_id', uid)
          .eq('onboarding_type', 'volunteer');

        const completedIds = new Set((completions || []).map((c: any) => c.entity_id));
        const uncompleted = eventIds.find(id => !completedIds.has(id));

        if (uncompleted) {
          const { data: evt } = await supabase
            .from('festivals')
            .select('name')
            .eq('id', uncompleted)
            .single();

          setPending({
            type: 'volunteer',
            entityId: uncompleted,
            eventName: (evt as any)?.name || 'the event',
          });
          setShowWizard(true);
          return;
        }
      }
    } catch (err) {
      console.error('Error checking speaker/volunteer onboarding:', err);
    }
  };

  if (!pending || !showWizard || !userId) return null;

  return (
    <OnboardingWizard
      open={showWizard}
      onClose={() => {
        setShowWizard(false);
        setPending(null);
      }}
      onboardingType={pending.type}
      eventName={pending.eventName}
      completionUserId={userId}
      completionEntityId={pending.entityId}
    />
  );
};
